import axios from "axios";
import {
  BadgeCheck,
  Ban,
  ClipboardList,
  Edit,
  Eye,
  IndianRupee,
  MoreVertical,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import Heading from "../../../components/AdminSideComponents/Heading";
import AddServiceDrawer from "../../../components/AdminSideComponents/services/AddServiceDrawer";
import ViewServiceModal from "../../../components/AdminSideComponents/services/ViewServiceModal";
import StatsCard from "../../../components/AdminSideComponents/StatCards";
import {
  confirmAction,
  showError,
  showSuccess,
} from "../../../utils/toastUtils";

const transformServices = (services = []) => {
  if (!Array.isArray(services)) return [];

  return services.map((service) => {
    return {
      id: service._id,
      raw: service,
      serviceName: service?.name,
      categoryName: service?.categoryId?.categoryName,
      subCategoryName: service?.subCategoryId?.subCategoryName,
      duration: service?.duration,
      thumbnail: service?.imageUrl,
      status: service?.status,

      pricingMode: service.pricingMode,
      originalPrice: service.originalPrice,
      discountedPrice: service.discountedPrice,
      variants: service?.type,
      extraCharges: service?.extraCharges,
    };
  });
};

const Services = () => {
  const DROPDOWN_FLIP_THRESHOLD = 3;
  const [services, setServices] = useState([]);
  const [isAddServiceOpen, setIsAddServiceOpen] = useState(false);
  const [editingService, setEditingService] = useState(false);
  const [isViewServiceOpen, setIsViewServiceOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [filteredServices, setFilteredServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    category: "",
    subCategory: "",
    status: "",
    search: "",
  });

  const ITEMS_PER_PAGE = 8;
  const totalPages = Math.ceil(filteredServices.length / ITEMS_PER_PAGE);
  const paginatedServices = filteredServices.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const fetchServices = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/get-all`,
      );

      const data = response?.data?.data ?? [];
      const transformedServices = transformServices(data);

      setServices(transformedServices);
      setFilteredServices(transformedServices);
    } catch (error) {
      console.error("Failed to fetch services", error);
      setServices([]);
      setFilteredServices([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleViewService = (service) => {
    setSelectedService(service?.raw);
    setIsViewServiceOpen(true);
    setDropdownOpen(null);
  };

  const handleEditService = (service) => {
    setDropdownOpen(null);
    setEditingService(service?.raw);
    setIsAddServiceOpen(true);
  };

  const handleSearch = (term) => {
    setFilters((prev) => ({
      ...prev,
      search: term,
    }));
  };
  useEffect(() => {
    let result = [...services];

    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(
        (s) =>
          s.serviceName?.toLowerCase().includes(q) ||
          s.categoryName?.toLowerCase().includes(q) ||
          s.subCategoryName?.toLowerCase().includes(q),
      );
    }

    if (filters.category) {
      result = result.filter((s) => s.categoryName === filters.category);
    }

    if (filters.subCategory) {
      result = result.filter((s) => s.subCategoryName === filters.subCategory);
    }

    if (filters.status) {
      result = result.filter((s) => s.status === filters.status);
    }

    setFilteredServices(result);
    setCurrentPage(1);
  }, [filters, services]);

  const handleDeleteService = async (service) => {
    setDropdownOpen(null);

    const confirmed = await confirmAction({
      title: "Delete Service?",
      text: `This will permanently delete "${service.serviceName}".`,
      confirmText: "Delete",
      cancelText: "Cancel",
      icon: "warning",
    });

    if (!confirmed) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/services/delete/${service.id}`,
      );

      showSuccess("Service deleted successfully");
      fetchServices();
    } catch (error) {
      console.error("Delete service failed:", error);
      showError(error?.response?.data?.message || "Failed to delete service");
    }
  };

  const categories = [
    ...new Set(services.map((s) => s.categoryName).filter(Boolean)),
  ];
  const subCategories = [
    ...new Set(services.map((s) => s.subCategoryName).filter(Boolean)),
  ];

  const filterActions = () => {
    return (
      <div className="flex items-center gap-3">
        {/* Clear All */}
        {(filters.search ||
          filters.category ||
          filters.subCategory ||
          filters.status) && (
          <button
            onClick={() =>
              setFilters({
                search: "",
                category: "",
                subCategory: "",
                status: "",
              })
            }
            className="
          px-3 py-3 text-sm rounded-md border
          text-red-600 border-red-300
          hover:bg-red-50 transition
        "
          >
            Clear All
          </button>
        )}
        {/* Category */}
        <select
          value={filters.category}
          onChange={(e) =>
            setFilters((p) => ({ ...p, category: e.target.value }))
          }
          className="
        px-3 py-3 text-sm rounded-md border border-gray-300
        bg-white
        focus:outline-none focus:ring-1 focus:ring-accentTextColor
      "
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>

        {/* SubCategory */}
        <select
          value={filters.subCategory}
          onChange={(e) =>
            setFilters((p) => ({ ...p, subCategory: e.target.value }))
          }
          className="
        px-3 py-3 text-sm rounded-md border border-gray-300
        bg-white
        focus:outline-none focus:ring-1 focus:ring-accentTextColor
      "
        >
          <option value="">All SubCategories</option>
          {subCategories.map((sub) => (
            <option key={sub} value={sub}>
              {sub}
            </option>
          ))}
        </select>

        {/* Status */}
        <select
          value={filters.status}
          onChange={(e) =>
            setFilters((p) => ({ ...p, status: e.target.value }))
          }
          className="
        px-3 py-3 text-sm rounded-md border border-gray-300
        bg-white
        focus:outline-none focus:ring-1 focus:ring-accentTextColor
      "
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
    );
  };
  return (
    <div className="bg-gradient-to-br px-6 min-h-screen from-[#FFF7F3] to-gray-50">
      <div className=" py-2 ">
        <Breadcrumb title1={"Services"} title2={"Service Management"} />
      </div>

      <div className=" mb-6 rounded-md bg-white shadow-lg p-6">
        <StatsCard
          title1={"Total Service Booked"}
          icon1={ClipboardList}
          value1={140}
          title2={"Revenue"}
          icon2={IndianRupee}
          value2={100000}
          title3={"Completed"}
          value3={120}
          icon3={BadgeCheck}
          title4={"Cancelled"}
          icon4={Ban}
          value4={20}
        />

        <Heading
          showButton={true}
          onSearch={handleSearch}
          buttonName="Add Service"
          customActions={filterActions}
          onButtonClick={() => {
            setEditingService(null);
            setIsAddServiceOpen(true);
          }}
        />

        <div className="overflow-x-auto overflow-y-visible no-scrollbar min-h-[20rem]">
          <table className="w-full min-w-max border border-gray-300 table-fixed">
            <thead>
              <tr className="text-left bg-[#EDB5A4] text-primaryTextColor text-xs uppercase tracking-wider">
                <th className="p-2 text-center w-12">Sl No</th>
                <th className="p-1 w-16"></th>
                <th className="p-3 w-68">Service Name</th>
                <th className="p-3 w-40">Category</th>
                <th className="p-3 w-40">SubCategory</th>
                <th className="py-3 pl-6 w-32">Status</th>
                <th className="p-3 w-32">Duration</th>
                <th className="p-3 text-center w-36">Pricing</th>
                <th className="p-3 text-center w-20">Actions</th>
              </tr>
            </thead>

            {isLoading ? (
              <ServicesTableSkeleton rows={ITEMS_PER_PAGE} />
            ) : services?.length === 0 ? (
              <EmptyState />
            ) : (
              <tbody className="divide-y divide-gray-200 text-sm">
                {paginatedServices?.map((service, index) => {
                  const shouldOpenUpwards =
                    paginatedServices.length >= DROPDOWN_FLIP_THRESHOLD &&
                    index >= paginatedServices.length - 2;

                  return (
                    <tr
                      key={service.id}
                      className="hover:bg-gray-50 transition"
                    >
                      <td className="p-3 text-center">
                        {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                      </td>
                      <td className="p-2 align-middle text-center w-16">
                        <img
                          src={service?.thumbnail}
                          className="inline-block w-12 h-8 rounded-lg object-cover"
                          alt=""
                        />
                      </td>

                      <td
                        onClick={() => handleViewService(service)}
                        className="p-4 font-medium cursor-pointer"
                      >
                        <div className="w-72 overflow-hidden whitespace-nowrap text-ellipsis">
                          {service.serviceName}
                        </div>
                      </td>

                      <td className="p-4">{service?.categoryName}</td>
                      <td className="p-4">
                        <div className="w-40 overflow-hidden whitespace-nowrap text-ellipsis">
                          {service?.subCategoryName}
                        </div>
                      </td>
                      <td className="p-4">
                        <span
                          className={`inline-flex items-center justify-center
                                  h-6 min-w-[72px] px-3 text-xs font-medium rounded-full
                                  ${
                                    service.status === "active"
                                      ? "bg-green-50 text-green-700 border border-green-300"
                                      : "bg-red-50 text-red-700 border border-red-300"
                                  }`}
                        >
                          {service.status}
                        </span>
                      </td>

                      <td className="p-4">{service.duration}</td>
                      <td
                        onClick={() => handleViewService(service)}
                        className="p-4 text-center cursor-pointer "
                      >
                        {service?.pricingMode === "single" ? (
                          <div className="text-sm">
                            <div className="font-medium">
                              ₹{service.discountedPrice}
                            </div>
                            {service.originalPrice !==
                              service.discountedPrice && (
                              <div className="text-xs text-gray-500 line-through">
                                ₹{service.originalPrice}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-2 text-slate-600">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                            >
                              <path d="M3 7h18M6 12h12M9 17h6" />
                            </svg>
                            <span className="text-xs font-semibold">
                              Multiple Prices
                            </span>
                          </div>
                        )}
                      </td>

                      <td className="p-4 text-center relative">
                        <button
                          onClick={() =>
                            setDropdownOpen(
                              dropdownOpen === service.id ? null : service.id,
                            )
                          }
                          className="p-2 hover:bg-gray-200 rounded-lg transition"
                        >
                          <MoreVertical size={20} className="text-gray-600" />
                        </button>

                        {/* Dropdown Menu */}
                        {dropdownOpen === service.id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setDropdownOpen(null)}
                            />
                            <div
                              className={`absolute right-8 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50 ${
                                shouldOpenUpwards ? "bottom-[80%]" : "top-14"
                              }`}
                            >
                              <button
                                onClick={() => handleViewService(service)}
                                className="w-full text-left px-4 py-3 hover:bg-gray-100 flex items-center gap-3"
                              >
                                <Eye size={16} />
                                View Service
                              </button>
                              <button
                                onClick={() => handleEditService(service)}
                                className="w-full text-left px-4 py-3 hover:bg-gray-100 flex items-center gap-3"
                              >
                                <Edit size={16} />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteService(service)}
                                className="w-full text-left px-4 py-3 hover:bg-red-50 text-red-600 flex items-center gap-3"
                              >
                                <Trash2 size={16} />
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <p className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </p>

            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-4 py-2 border rounded-lg disabled:opacity-50"
              >
                Prev
              </button>

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-4 py-2 border rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Add Service Drawer */}
        <AddServiceDrawer
          isOpen={isAddServiceOpen}
          editingService={editingService}
          onAddService={fetchServices}
          onClose={() => {
            setIsAddServiceOpen(false);
            setEditingService(null);
          }}
        />

        {/* View Service Modal */}
        <ViewServiceModal
          isOpen={isViewServiceOpen}
          onClose={() => setIsViewServiceOpen(false)}
          service={selectedService}
        />
      </div>
    </div>
  );
};

const ServicesTableSkeleton = ({ rows = 8 }) => {
  return (
    <tbody className="divide-y divide-gray-200">
      {Array.from({ length: rows }).map((_, i) => (
        <tr
          key={i}
          className="animate-pulse"
          style={{
            animationDelay: `${i * 80}ms`, // subtle stagger between rows
          }}
        >
          {/* Sl No */}
          <td className="p-3 text-center">
            <div className="h-4 w-6 bg-gray-200 rounded mx-auto" />
          </td>

          {/* Thumbnail */}
          <td className="p-2 text-center align-middle">
            <div className="w-12 h-8 bg-gray-200 rounded-lg mx-auto" />
          </td>

          {/* Service Name – longest content */}
          <td className="p-4">
            <div className="h-4 w-3/4 max-w-[260px] bg-gray-200 rounded" />
          </td>

          {/* Category */}
          <td className="p-4">
            <div className="h-4 w-28 bg-gray-200 rounded" />
          </td>

          {/* SubCategory */}
          <td className="p-4">
            <div className="h-4 w-32 bg-gray-200 rounded" />
          </td>

          {/* Status badge */}
          <td className="p-4">
            <div className="h-6 w-20 bg-gray-200 rounded-full mx-auto" />
          </td>

          {/* Duration */}
          <td className="p-4">
            <div className="h-4 w-16 bg-gray-200 rounded mx-auto" />
          </td>

          {/* Pricing */}
          <td className="p-4">
            <div className="h-5 w-20 bg-gray-200 rounded mx-auto" />
          </td>

          {/* Actions (3-dot button) */}
          <td className="p-4 text-center">
            <div className="h-8 w-8 bg-gray-200 rounded-full mx-auto" />
          </td>
        </tr>
      ))}
    </tbody>
  );
};

const EmptyState = ({ title, description }) => {
  return (
    <tbody>
      <tr>
        <td colSpan={8} className="py-16 text-center">
          <div className="flex flex-col items-center justify-center gap-4 text-gray-500">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
              <ClipboardList className="w-8 h-8 text-gray-400" />
            </div>

            <h3 className="text-lg font-semibold text-gray-700">
              {title || "No services found"}
            </h3>

            <p className="text-sm max-w-md">
              {description ||
                "You haven’t added any services yet. Start by adding your first service to make it visible here."}
            </p>
          </div>
        </td>
      </tr>
    </tbody>
  );
};

export default Services;
